/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from 'react';
import {
  getAnnouncements,
  markAnnouncementAsRead,
  markAllAnnouncementsAsRead,
  type Announcement
} from '@/api';
import { useAuth } from '@/hooks/useAuth';

const TYPE_CONFIG = {
  info: { color: '#0ea5e9', bg: 'bg-[#0ea5e9]/10', border: 'border-[#0ea5e9]/20', icon: 'ri-information-line', btnBg: 'bg-[#0ea5e9] hover:bg-[#0284c7]' },
  warning: { color: '#f59e0b', bg: 'bg-amber-50', border: 'border-amber-200', icon: 'ri-alarm-warning-line', btnBg: 'bg-amber-500 hover:bg-amber-600' },
  success: { color: '#10b981', bg: 'bg-[#10b981]/10', border: 'border-[#10b981]/20', icon: 'ri-sparkling-line', btnBg: 'bg-[#10b981] hover:bg-[#059669]' },
  event: { color: '#f97316', bg: 'bg-[#f97316]/10', border: 'border-[#f97316]/20', icon: 'ri-gift-line', btnBg: 'bg-[#f97316] hover:bg-[#ea6c0a]' },
};

export default function AnnouncementPopup() {
  const { isLoggedIn, user } = useAuth();
  const [queue, setQueue] = useState<Announcement[]>([]);
  const [current, setCurrent] = useState<Announcement | null>(null);

  const loadUnseenAnnouncements = () => {
    if (!isLoggedIn) return;
    getAnnouncements()
      .then((activeUnread) => {
        setQueue(activeUnread);
        if (activeUnread.length > 0) {
          setCurrent((curr) => curr || activeUnread[0]);
        }
      })
      .catch((err) => {
        console.error('Failed to load active announcements:', err);
      });
  };

  useEffect(() => {
    if (!isLoggedIn) return;

    // 轻微延迟触发，确保主页面元素完全渲染加载完毕
    const timer = setTimeout(loadUnseenAnnouncements, 1200);
    return () => clearTimeout(timer);
  }, [isLoggedIn, user]);

  useEffect(() => {
    const handleUpdate = () => {
      loadUnseenAnnouncements();
    };
    window.addEventListener('wanxiang_announcements_updated', handleUpdate);
    return () => window.removeEventListener('wanxiang_announcements_updated', handleUpdate);
  }, [isLoggedIn, user]);

  const handleClose = () => {
    if (!current) return;
    markAnnouncementAsRead(current.id)
      .catch((err) => console.error('Failed to mark announcement as read:', err));
    const remaining = queue.filter((a) => a.id !== current.id);
    setQueue(remaining);
    setCurrent(remaining.length > 0 ? remaining[0] : null);
  };

  if (!current || !isLoggedIn) return null;

  const cfg = TYPE_CONFIG[current.type];
  const remaining = queue.length;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[200] p-4" onClick={handleClose}>
      <div
        className={`bg-white rounded-2xl w-full max-w-md border-2 ${cfg.border} overflow-hidden`}
        onClick={(e) => e.stopPropagation()}
        style={{ animation: 'fadeInScale 0.3s ease-out' }}
      >
        {/* 公告卡片顶部头部 */}
        <div className={`${cfg.bg} px-6 py-4 flex items-center justify-between`}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-white rounded-xl">
              <i className={`${cfg.icon} text-xl`} style={{ color: cfg.color }}></i>
            </div>
            <div>
              <p className="text-xs font-semibold" style={{ color: cfg.color }}>平台公告</p>
              <p className="text-xs text-gray-400 mt-0.5">发布于 {current.publishedAt}</p>
            </div>
          </div>
          {remaining > 1 && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-white/60 font-medium" style={{ color: cfg.color }}>
              {remaining} 条未读
            </span>
          )}
        </div>

        {/* 公告卡片主文本内容 */}
        <div className="px-6 py-5">
          <h3 className="font-bold text-gray-900 text-lg mb-3 leading-tight">{current.title}</h3>
          <p className="text-gray-600 text-sm leading-relaxed">{current.content}</p>
        </div>

        {/* 公告卡片底部操作按钮 */}
        <div className="px-6 pb-5 flex gap-3">
          {remaining > 1 ? (
            <>
              <button onClick={handleClose}
                className="flex-1 py-2.5 border border-gray-200 text-gray-600 text-sm font-medium rounded-xl hover:bg-gray-50 cursor-pointer whitespace-nowrap">
                下一条
              </button>
              <button onClick={() => {
                const ids = queue.map((a) => a.id);
                markAllAnnouncementsAsRead(ids)
                  .catch((err) => console.error('Failed to mark all as read:', err));
                setQueue([]);
                setCurrent(null);
              }}
                className={`flex-1 py-2.5 text-white text-sm font-medium rounded-xl cursor-pointer whitespace-nowrap transition-colors ${cfg.btnBg}`}>
                全部已读
              </button>
            </>
          ) : (
            <button onClick={handleClose}
              className={`w-full py-2.5 text-white text-sm font-medium rounded-xl cursor-pointer whitespace-nowrap transition-colors ${cfg.btnBg}`}>
              我知道了
            </button>
          )}
        </div>
      </div>

      <style>{`
        @keyframes fadeInScale {
          from { opacity: 0; transform: scale(0.92); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
}
